/**
 * useWithdrawalPolling - Hook for polling withdrawal status updates
 *
 * Automatically polls for status changes when there are pending/processing withdrawals.
 * Triggers callbacks when withdrawals complete or fail.
 */

import { useEffect, useRef, useCallback } from 'react';
import apiService, { WithdrawalRequest } from '../services/api';

interface UseWithdrawalPollingOptions {
  /** Current list of withdrawals */
  withdrawals: WithdrawalRequest[];
  /** Callback when withdrawals list is updated */
  onWithdrawalsUpdate: (withdrawals: WithdrawalRequest[]) => void;
  /** Callback when a withdrawal completes successfully */
  onComplete?: (withdrawal: WithdrawalRequest) => void;
  /** Callback when a withdrawal fails */
  onFailed?: (withdrawal: WithdrawalRequest) => void;
  /** Polling interval in milliseconds (default: 3000) */
  pollInterval?: number;
  /** Whether polling is enabled (default: true) */
  enabled?: boolean;
}

export const useWithdrawalPolling = ({
  withdrawals,
  onWithdrawalsUpdate,
  onComplete,
  onFailed,
  pollInterval = 3000,
  enabled = true,
}: UseWithdrawalPollingOptions): void => {
  // Use ref to track previous withdrawals to avoid stale closure issues
  const previousWithdrawalsRef = useRef<WithdrawalRequest[]>(withdrawals);

  // Update ref when withdrawals change
  useEffect(() => {
    previousWithdrawalsRef.current = withdrawals;
  }, [withdrawals]);

  const pollWithdrawals = useCallback(async () => {
    try {
      const withdrawalsData = await apiService.withdrawals.getList();
      const previousWithdrawals = previousWithdrawalsRef.current;

      // Check for status changes before updating
      for (const w of withdrawalsData) {
        const prev = previousWithdrawals.find(pw => pw.id === w.id);
        if (prev && (prev.status === 'pending' || prev.status === 'processing')) {
          if (w.status === 'completed' && onComplete) {
            onComplete(w);
          } else if (w.status === 'failed' && onFailed) {
            onFailed(w);
          }
        }
      }

      onWithdrawalsUpdate(withdrawalsData);
    } catch (err) {
      console.error('Error polling withdrawal status:', err);
    }
  }, [onWithdrawalsUpdate, onComplete, onFailed]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const hasPendingWithdrawals = withdrawals.some(
      w => w.status === 'pending' || w.status === 'processing'
    );

    if (!hasPendingWithdrawals) {
      return;
    }

    const intervalId = setInterval(pollWithdrawals, pollInterval);

    return () => clearInterval(intervalId);
  }, [withdrawals, pollWithdrawals, pollInterval, enabled]);
};

export default useWithdrawalPolling;
